import { useEffect, useMemo, useState } from "react";
import {
  CHEEP_HOSTED_LUXURY_PLACEHOLDER,
  CHEEP_LUXURY_PLACEHOLDER_DATA_URI,
  buildProxyImageUrl,
  collectProductGalleryUrls,
  hasVerifiedSupplierImage,
} from "../lib/productImageAssets";
import {
  getDefaultVariationSelection,
  getLockedProductImage,
  isVariationLocked,
} from "../lib/variationImageLock";

/**
 * Product image with verified badge + branded placeholder fallback.
 * Master prompt v2.1 / Rule 6:
 *   - On any vendor-link failure swap instantly to the hosted PNG
 *     placeholder. If that also 404s, fall through to the inline data URI
 *     so a card *always* renders.
 *   - The "✦ Real Product View" badge is suppressed unless the image was
 *     structurally validated against the exact vendor SKU (resolved by
 *     `hasVerifiedSupplierImage`, which also re-checks the URL is not a
 *     brand asset or stock-photo host).
 */
export default function LuxuryProductImage({
  product,
  alt,
  className = "w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105",
  showBadge = true,
  testId,
}) {
  const gallery = useMemo(() => collectProductGalleryUrls(product), [product]);
  const lockedImage = useMemo(
    () =>
      getLockedProductImage(
        product,
        getDefaultVariationSelection(product?.variation_matrix)
      ),
    [product]
  );
  const initial = lockedImage || gallery[0] || CHEEP_HOSTED_LUXURY_PLACEHOLDER;
  const [imgSrc, setImgSrc] = useState(initial);
  const [errorStage, setErrorStage] = useState(0);
  const showVerifiedBadge = showBadge && hasVerifiedSupplierImage(product);

  useEffect(() => {
    setImgSrc(initial);
    setErrorStage(0);
  }, [initial, product?.product_id]);

  const handleError = () => {
    if (isVariationLocked(product)) {
      if (imgSrc !== CHEEP_HOSTED_LUXURY_PLACEHOLDER) {
        setImgSrc(CHEEP_HOSTED_LUXURY_PLACEHOLDER);
      }
      return;
    }
    if (errorStage === 0 && gallery.length > 1) {
      setErrorStage(1);
      setImgSrc(gallery[1]);
      return;
    }
    if (errorStage <= 1 && gallery[0]) {
      setErrorStage(2);
      setImgSrc(buildProxyImageUrl(gallery[0]));
      return;
    }
    if (errorStage <= 2 && imgSrc !== CHEEP_HOSTED_LUXURY_PLACEHOLDER) {
      setErrorStage(3);
      setImgSrc(CHEEP_HOSTED_LUXURY_PLACEHOLDER);
      return;
    }
    if (errorStage <= 3 && imgSrc !== CHEEP_LUXURY_PLACEHOLDER_DATA_URI) {
      setErrorStage(4);
      setImgSrc(CHEEP_LUXURY_PLACEHOLDER_DATA_URI);
    }
  };

  return (
    <div className="relative w-full h-full">
      <img
        src={imgSrc}
        alt={alt || product?.name || "Product"}
        className={className}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        data-testid={testId}
        onError={handleError}
      />
      {showVerifiedBadge && (
        <span
          className="absolute top-2 right-2 bg-emerald-500/15 backdrop-blur-md text-emerald-700 border border-emerald-500/30 px-2 py-0.5 rounded text-[9px] font-semibold tracking-wider uppercase pointer-events-none"
          aria-label="SKU-verified vendor photo"
        >
          ✦ Real Product View
        </span>
      )}
    </div>
  );
}
